/**
 * 表格日期欄位的「顯示 / 手動編輯」格式。
 *
 * 專案內有兩個方向的日期處理,刻意採用不同寬鬆度,不可混為一談:
 *
 * - **輸入端(貼上匯入)**:paste-import.ts 支援多種來源格式(含 `mm-dd-yy`、
 *   `yyyy/m/d` 等),因為外部 Excel / MS Project 匯出表的格式無法要求使用者統一。
 * - **顯示與手動編輯端(本檔)**:一律使用 `yyyy-mm-dd`,不隨瀏覽器 locale 變動。
 *   原生 `<input type="date">` 會依 locale 顯示成 `mm/dd/yyyy`,與表格內其他地方
 *   的顯示格式不一致,因此改用純文字輸入 + 本檔的解析。
 */

const DISPLAY_LENGTH = 10;

function pad2(value: string): string {
  return value.length === 1 ? `0${value}` : value;
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  if (!Number.isInteger(day) || day < 1 || day > 31) return false;
  // 用 UTC 建構回推,擋掉 2026-02-30 這種「數字合法但日期不存在」的輸入。
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day
  );
}

/**
 * 將任務欄位的日期值(ISO datetime 或 date 字串)格式化為 `yyyy-mm-dd`。
 *
 * 容忍未補零的來源(例如 `2026-7-4`),一律補成兩位數;完全無法辨識時回傳原字串
 * 的前 10 個字元(保底,不讓儲存格變空白而使資料看起來遺失)。
 */
export function formatDateDisplay(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (text === '') return '';

  const match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/.exec(text);
  if (match) {
    const [, year, month, day] = match;
    if (year !== undefined && month !== undefined && day !== undefined) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
  }
  return text.slice(0, DISPLAY_LENGTH);
}

/**
 * 解析手動編輯輸入的日期,回傳正規化的 `yyyy-mm-dd`;無法解析時回傳 null。
 *
 * 接受使用者原話提到的兩種寫法:`yyyy-mm-dd` 與 `yyyy/mm/dd`(另接受 `.` 分隔與
 * 未補零),不接受 `mm-dd-yy` 這類**輸入端**才需要的寬鬆格式 —— 手動編輯若也允許
 * 兩種年月日順序,`03-04-2026` 會有兩種合理解讀,只能猜,而猜錯會靜默寫壞資料。
 */
export function parseDateInput(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  const match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(trimmed);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  if (yearRaw === undefined || monthRaw === undefined || dayRaw === undefined) return null;

  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!isValidYmd(year, month, day)) return null;

  return `${yearRaw}-${pad2(monthRaw)}-${pad2(dayRaw)}`;
}
